import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getCeremony, saveCeremonyResult, generateCertificates, publishCeremony, assetUrl,
} from '../../services/api';
import { RUBRIC_CATEGORIES, scoreResult, MAX_TOTAL, PASS_TOTAL } from '../../utils/scoring';
import { getRumaalColor } from '../../utils/colors';
import AddStudents from './AddStudents';

// Scoring a ceremony, live.
//
// The admin does this on a laptop while running the event, with parents
// watching and venue wifi that drops. Everything here follows from that:
// scores save themselves the moment they are typed, a failed save keeps
// retrying instead of demanding attention, and a copy is kept in the browser
// so nothing is lost even if the tab is closed mid-group.
//
// Students are worked in groups of five, which is how the academy actually
// calls them up.

const GROUP_SIZE = 5;
const draftKey = (ceremonyId) => `l2b-ceremony-draft:${ceremonyId}`;

// Header, score line and notes line all share this, so a note always sits
// directly under the score it belongs to.
const GRID = 'grid grid-cols-[minmax(200px,1.6fr)_70px_repeat(4,1fr)_90px_70px_110px] gap-2';

const CELL = 'w-full rounded-md border text-center text-lg font-semibold outline-none transition-colors py-2';

const NOTE_FIELDS = RUBRIC_CATEGORIES.map((c) => `${c.key}Notes`).concat('overallNotes');
const countNotes = (student) => NOTE_FIELDS.filter((f) => (student[f] || '').trim()).length;

/** Accepts what an admin types — "8", "8.5", ".5", "" — and nothing else. */
function parseScore(raw) {
  const text = String(raw).trim();
  if (text === '') return { value: null };
  if (!/^\d{0,2}(\.\d?)?$/.test(text)) return { invalid: true };
  const value = Number(text);
  if (!Number.isFinite(value) || value < 0 || value > 10) return { invalid: true };
  // Half-points only: 7.25 is a typo, not a judgement.
  if (Math.abs(value * 2 - Math.round(value * 2)) > 1e-9) return { invalid: true };
  return { value };
}

export default function ScoringGrid({ ceremonyId, onBack }) {
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState('');
  const [group, setGroup] = useState(0);
  const [saveState, setSaveState] = useState({}); // studentKey -> saving|saved|error
  const [pendingDraft, setPendingDraft] = useState(false);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null);
  const [adding, setAdding] = useState(false);
  // Which students have their notes open. Scoring is the job; notes are the
  // exception, so they stay out of the way — and out of the tab order — until
  // the admin asks for them on a particular child.
  const [notesOpen, setNotesOpen] = useState(() => new Set());

  const timers = useRef({});
  const retries = useRef({});
  // Edits waiting to go to the server, merged per student. Scores and notes now
  // sit side by side, so a note typed just before a score must not be dropped
  // when the score restarts the debounce.
  const pending = useRef({});

  const load = useCallback(async () => {
    try {
      const { data } = await getCeremony(ceremonyId);
      // A draft left by a dropped connection wins over the server copy — it is
      // the more recent truth, and replaying it is what makes a lost evening
      // impossible.
      const draft = readDraft(ceremonyId);
      if (draft && Object.keys(draft).length > 0) {
        data.students = data.students.map((s) =>
          (draft[s.studentKey] ? { ...s, ...draft[s.studentKey] } : s));
        setPendingDraft(true);
      }
      setPayload(data);
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Could not load this ceremony');
    }
  }, [ceremonyId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = timers.current;
    const r = retries.current;
    return () => {
      Object.values(t).forEach(clearTimeout);
      Object.values(r).forEach(clearTimeout);
    };
  }, []);

  // Memoised so the fallback array doesn't hand the grouping a new reference
  // on every render.
  const students = useMemo(() => payload?.students || [], [payload]);
  const groups = useMemo(() => {
    const out = [];
    for (let i = 0; i < students.length; i += GROUP_SIZE) out.push(students.slice(i, i + GROUP_SIZE));
    return out;
  }, [students]);

  const current = groups[group] || [];

  // Counted from what is on screen rather than the snapshot the server sent
  // when the page loaded — otherwise the admin scores a whole group and the
  // tally, and the buttons it gates, stay stubbornly at zero until a reload.
  // Same scoring module as the server, so the two always agree.
  const summary = useMemo(() => {
    const scored = students.map((s) => ({ student: s, result: scoreResult(s) }));
    const complete = scored.filter(({ result }) => result.complete);
    return {
      total: students.length,
      scored: complete.length,
      passed: complete.filter(({ result }) => result.passed).length,
      inProgress: complete.filter(({ result }) => !result.passed).length,
      absent: scored.filter(({ result }) => !result.attended).length,
      certificatesDue: complete.filter(({ student }) => student.certificateStatus !== 'generated').length,
    };
  }, [students]);

  /** Update one field locally, keep a draft, and schedule the save. */
  const change = (studentKey, field, value) => {
    setPayload((p) => ({
      ...p,
      students: p.students.map((s) => (s.studentKey === studentKey ? { ...s, [field]: value } : s)),
    }));

    writeDraft(ceremonyId, studentKey, { [field]: value });
    setSaveState((s) => ({ ...s, [studentKey]: 'saving' }));

    pending.current[studentKey] = { ...(pending.current[studentKey] || {}), [field]: value };
    clearTimeout(timers.current[studentKey]);
    timers.current[studentKey] = setTimeout(() => push(studentKey), 600);
  };

  const push = useCallback(async (studentKey) => {
    const changes = pending.current[studentKey];
    if (!changes) return;
    // Off the queue while it is in flight, and back on if the save fails.
    delete pending.current[studentKey];

    try {
      const { data } = await saveCeremonyResult(ceremonyId, studentKey, changes);
      setPayload((p) => (p ? {
        ...p,
        students: p.students.map((s) => (s.studentKey === studentKey ? data.student : s)),
      } : p));
      // Only the fields that actually landed — anything typed since is still
      // queued, and its draft is the only copy until it saves too.
      clearDraft(ceremonyId, studentKey, Object.keys(changes));
      if (!pending.current[studentKey]) {
        setSaveState((s) => ({ ...s, [studentKey]: 'saved' }));
        clearTimeout(retries.current[studentKey]);
      }
    } catch (saveError) {
      setSaveState((s) => ({ ...s, [studentKey]: 'error' }));
      const rejected = saveError.response?.status === 400;
      // A rejected value is the admin's to fix; anything else is the network,
      // so keep trying quietly rather than interrupting them mid-ceremony.
      if (!rejected) {
        // Newer keystrokes win over the ones that failed.
        pending.current[studentKey] = { ...changes, ...(pending.current[studentKey] || {}) };
        clearTimeout(retries.current[studentKey]);
        retries.current[studentKey] = setTimeout(() => push(studentKey), 4000);
      }
    }
  }, [ceremonyId]);

  const setScore = (student, key, raw) => {
    const parsed = parseScore(raw);
    if (parsed.invalid) return; // refuse as it is typed, like the design says
    change(student.studentKey, key, parsed.value);
  };

  const toggleAttended = (student) => change(student.studentKey, 'attended', !student.attended);

  const toggleNotes = (studentKey) => setNotesOpen((open) => {
    const next = new Set(open);
    if (next.has(studentKey)) next.delete(studentKey); else next.add(studentKey);
    return next;
  });

  const runCertificates = async () => {
    setBusy('certificates');
    setMessage(null);
    try {
      const { data } = await generateCertificates(ceremonyId, {});
      setMessage({
        kind: 'ok',
        text: `${data.generated} certificate${data.generated === 1 ? '' : 's'} generated.` +
          (data.skipped.length ? ` ${data.skipped.length} skipped.` : '') +
          (data.failed.length ? ` ${data.failed.length} failed.` : ''),
      });
      load();
    } catch (certError) {
      setMessage({ kind: 'bad', text: certError.response?.data?.error || 'Could not generate certificates' });
    } finally {
      setBusy('');
    }
  };

  const publish = async (force = false) => {
    setBusy('publish');
    setMessage(null);
    try {
      const { data } = await publishCeremony(ceremonyId, force ? { force: true } : {});
      setMessage({ kind: 'ok', text: `Published — ${data.published} results are now visible to parents.` });
      load();
    } catch (pubError) {
      const status = pubError.response?.status;
      if (status === 409) {
        setMessage({
          kind: 'warn',
          text: pubError.response.data.error,
          confirm: () => publish(true),
        });
      } else {
        setMessage({ kind: 'bad', text: pubError.response?.data?.error || 'Could not publish' });
      }
    } finally {
      setBusy('');
    }
  };

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">{error}</p>
        <button onClick={onBack} className="mt-4 rounded-md bg-gray-900 px-4 py-2 text-white">Back</button>
      </div>
    );
  }
  if (!payload) return <p className="p-8 text-gray-500">Loading…</p>;

  const { ceremony } = payload;
  const unsaved = Object.values(saveState).filter((s) => s === 'saving' || s === 'error').length;

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <button onClick={onBack} className="mb-2 text-sm text-purple-600 hover:text-purple-700">
            ← All ceremonies
          </button>
          <h2 className="text-2xl font-bold text-gray-900">{ceremony.name}</h2>
          <p className="text-sm text-gray-500">
            {students.length} testing · pass at {PASS_TOTAL}/{MAX_TOTAL} (75%)
            {ceremony.status === 'published' && ' · published'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-3 py-1.5 text-xs font-medium ${
            unsaved === 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'
          }`}>
            {unsaved === 0 ? 'All scores saved' : `${unsaved} saving…`}
          </span>
          <button
            onClick={() => setAdding(true)}
            className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            + Add students
          </button>
        </div>
      </div>

      {pendingDraft && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Unsaved scores from this device were restored. They will save themselves now.
        </div>
      )}

      {message && (
        <div className={`mb-3 rounded-lg border p-3 text-sm ${
          message.kind === 'ok' ? 'border-green-200 bg-green-50 text-green-900'
            : message.kind === 'warn' ? 'border-amber-200 bg-amber-50 text-amber-900'
              : 'border-red-200 bg-red-50 text-red-900'
        }`}>
          <span>{message.text}</span>
          {message.confirm && (
            <button
              onClick={message.confirm}
              className="ml-3 rounded-md bg-amber-700 px-2.5 py-1 text-xs font-medium text-white"
            >
              Publish anyway
            </button>
          )}
        </div>
      )}

      {/* Group stepper — the academy calls students up about five at a time. */}
      {groups.length > 1 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {groups.map((g, index) => {
            const done = g.every((s) => scoreResult(s).complete || !s.attended);
            return (
              <button
                key={index}
                onClick={() => setGroup(index)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  index === group ? 'bg-purple-600 text-white'
                    : done ? 'bg-gray-900/5 text-gray-500' : 'border border-gray-200 bg-white text-gray-700'
                }`}
              >
                Group {index + 1}{done && index !== group ? ' ✓' : ''}
              </button>
            );
          })}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <div className="min-w-[880px]">
          <div className={`${GRID} items-end border-b-2 border-gray-900 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500`}>
            <span>Student · testing for</span>
            <span className="text-center">Here?</span>
            {RUBRIC_CATEGORIES.map((c) => (
              <span key={c.key} className="text-center">{c.label} /10</span>
            ))}
            <span className="text-center text-gray-900">Total /40</span>
            <span className="text-center">%</span>
            <span className="text-center">Status</span>
          </div>

          {current.map((student) => (
            <Row
              key={student.studentKey}
              student={student}
              saveState={saveState[student.studentKey]}
              notesOpen={notesOpen.has(student.studentKey)}
              onScore={setScore}
              onToggle={toggleAttended}
              onToggleNotes={() => toggleNotes(student.studentKey)}
              onNote={(field, value) => change(student.studentKey, field, value)}
            />
          ))}

          {current.length === 0 && (
            <p className="p-8 text-center text-sm text-gray-500">
              Nobody is in this ceremony yet — add the students who are testing.
            </p>
          )}
        </div>
      </div>

      <p className="mt-2 text-xs text-gray-500">
        Scores are 0–10 in halves, and everything saves as you type. Tick{' '}
        <span className="font-medium text-gray-600">Notes</span> on a student to write feedback
        under each of their scores — Tab then steps score, note, score, note.
      </p>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4">
        <div className="text-sm text-gray-700">
          <strong>{summary.scored}</strong> scored ·{' '}
          <span className="text-green-700">{summary.passed} passed</span> ·{' '}
          <span className="text-amber-700">{summary.inProgress} in progress</span> ·{' '}
          {summary.absent} absent
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={runCertificates}
            disabled={busy !== '' || summary.scored === 0}
            className="rounded-md border-2 border-gray-900 px-4 py-2 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy === 'certificates' ? 'Generating…' : `Generate certificates (${summary.certificatesDue})`}
          </button>
          <button
            onClick={() => publish(false)}
            disabled={busy !== '' || summary.scored === 0}
            className="rounded-md bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy === 'publish' ? 'Publishing…' : ceremony.status === 'published' ? 'Re-publish' : 'Publish to parents'}
          </button>
        </div>
      </div>

      {adding && (
        <AddStudents
          ceremonyId={ceremonyId}
          onClose={() => setAdding(false)}
          onAdded={() => { setAdding(false); load(); }}
        />
      )}
    </div>
  );
}

function Row({ student, saveState, notesOpen, onScore, onToggle, onToggleNotes, onNote }) {
  const scored = scoreResult(student);
  const initials = student.name.split(' ').map((w) => w[0]).slice(0, 2).join('');
  const notes = countNotes(student);
  const row = useRef(null);

  // With notes open, Tab should follow the judging: score, then the note about
  // that score, then the next score. That is not the order they sit in the
  // document — the notes are a second line — and a positive tabIndex would
  // hoist them above everything else on the page, so the order is walked here
  // instead. Falling off either end hands back to the browser.
  const keepTabOrder = (event) => {
    if (event.key !== 'Tab' || !notesOpen || !student.attended) return;

    const ring = [...row.current.querySelectorAll('[data-ring]')]
      .sort((a, b) => Number(a.dataset.ring) - Number(b.dataset.ring));
    const here = ring.indexOf(event.target);
    if (here === -1) return;

    const next = ring[here + (event.shiftKey ? -1 : 1)];
    if (!next) return;
    event.preventDefault();
    next.focus(); // a score cell selects itself on focus; a note keeps its caret
  };

  return (
    <div
      ref={row}
      onKeyDown={keepTabOrder}
      className={`border-b border-gray-100 ${student.attended ? '' : 'bg-gray-50/60 opacity-60'}`}
    >
      <div className={`${GRID} items-center px-3 py-2`}>
        <div className="flex min-w-0 items-center gap-2">
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100"
            style={{ boxShadow: `0 0 0 2px ${getRumaalColor(student.rankAtTest)}, 0 0 0 3px rgba(0,0,0,.15)` }}
          >
            {student.photoUrl
              ? <img src={assetUrl(student.photoUrl)} alt="" className="h-full w-full object-cover" />
              : <span className="text-[10px] font-semibold text-gray-400">{initials}</span>}
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-gray-900">{student.name}</div>
            <div className="truncate text-xs text-gray-500">
              {student.rankAtTest || 'No rank'}
              {student.rankAtTest && ' → '}
              {student.rankAtTest && <span className="font-medium">{nextOf(student.rankAtTest)}</span>}
            </div>
            {/* Opt in per student. The count is what keeps a note that is
                already written from hiding behind a closed row. */}
            {student.attended && (
              <label className={`mt-1 flex w-fit cursor-pointer items-center gap-1.5 text-[11px] ${
                notes > 0 ? 'font-medium text-purple-700' : 'text-gray-400 hover:text-purple-600'
              }`}>
                <input
                  type="checkbox"
                  checked={notesOpen}
                  onChange={onToggleNotes}
                  className="h-3 w-3 accent-purple-600"
                  aria-label={`Notes for ${student.name}`}
                />
                Notes{notes > 0 ? ` · ${notes}` : ''}
              </label>
            )}
          </div>
        </div>

        {/* Here? — absence is a state, never a zero. */}
        <div className="flex justify-center">
          <button
            onClick={() => onToggle(student)}
            title={student.attended ? 'Mark as did not attend' : 'Mark as here'}
            className={`h-6 w-11 rounded-full transition-colors ${student.attended ? 'bg-green-600' : 'bg-gray-300'}`}
          >
            <span className={`block h-5 w-5 rounded-full bg-white transition-transform ${
              student.attended ? 'translate-x-[22px]' : 'translate-x-[2px]'
            }`} />
          </button>
        </div>

        {student.attended ? (
          RUBRIC_CATEGORIES.map((category, index) => (
            <ScoreCell
              key={category.key}
              value={student[category.key]}
              label={`${category.label} for ${student.name}`}
              ring={index * 2}
              onCommit={(value) => onScore(student, category.key, value)}
            />
          ))
        ) : (
          <div className="col-span-4 text-center text-sm text-gray-500">
            Did not attend — no scores recorded
          </div>
        )}

        <div className={`text-center text-lg font-bold ${scored.complete ? 'text-gray-900' : 'text-gray-300'}`}>
          {scored.total ?? '—'}
        </div>
        <div className={`text-center text-sm font-semibold ${
          scored.percentage === null ? 'text-gray-300' : scored.passed ? 'text-green-600' : 'text-amber-600'
        }`}>
          {scored.percentage === null ? '—' : `${scored.percentage}%`}
        </div>

        <div className="flex items-center justify-center gap-1">
          <StatusPill scored={scored} />
          <SaveDot state={saveState} />
        </div>
      </div>

      {/* Each note under the score it explains, on the same column template so
          the two line up exactly. */}
      {notesOpen && student.attended && (
        <div className={`${GRID} items-start border-t border-dashed border-gray-200 bg-gray-50/50 px-3 pb-3 pt-2`}>
          <div className="col-span-2" />
          {RUBRIC_CATEGORIES.map((category, index) => (
            <NoteField
              key={category.key}
              label={category.label}
              value={student[`${category.key}Notes`]}
              placeholder="Note…"
              ring={index * 2 + 1}
              onCommit={(value) => onNote(`${category.key}Notes`, value)}
            />
          ))}
          {/* Last in the row and last in the tab ring — it is written once the
              four scores and their notes are done. */}
          <div className="col-span-3">
            <NoteField
              label="Overall"
              value={student.overallNotes}
              placeholder="A few words the parent will read"
              ring={RUBRIC_CATEGORIES.length * 2}
              onCommit={(value) => onNote('overallNotes', value)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * One score cell.
 *
 * Holds the text being typed rather than the number, so "8." and "8.5" can be
 * reached a keystroke at a time. A keystroke that could not lead anywhere valid
 * is dropped and never appears — the cell must always show exactly what is
 * stored, because an admin scoring at speed will not stop to compare them.
 */
function ScoreCell({ value, label, ring, onCommit }) {
  const [text, setText] = useState(value ?? '');
  const [lastSeen, setLastSeen] = useState(value);

  // Follow the record when it changes underneath us — a restored draft, or a
  // save coming back from the server. Adjusted during render rather than in an
  // effect, which is React's own guidance for deriving state from a prop and
  // avoids the extra pass an effect would cost on every keystroke.
  if (value !== lastSeen) {
    setLastSeen(value);
    setText(value ?? '');
  }

  const handle = (raw) => {
    const trimmed = raw.trim();
    const parsed = parseScore(trimmed);
    if (parsed.invalid) return; // refuse as it is typed

    setText(trimmed);
    onCommit(trimmed);
  };

  const empty = value === null || value === undefined;

  return (
    <input
      value={text}
      onChange={(e) => handle(e.target.value)}
      onFocus={(e) => e.target.select()}
      inputMode="decimal"
      aria-label={label}
      data-ring={ring}
      className={`${CELL} ${
        empty ? 'border-dashed border-gray-300 text-gray-400' : 'border-gray-300 text-gray-900'
      } focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20`}
    />
  );
}

/**
 * A note, written where it was earned rather than in a panel somewhere else.
 * Mirrors ScoreCell's render-time derivation so a restored draft or a save
 * coming back from the server is picked up without an effect.
 */
function NoteField({ value, label, placeholder, ring, onCommit }) {
  const [text, setText] = useState(value ?? '');
  const [lastSeen, setLastSeen] = useState(value);

  if (value !== lastSeen) {
    setLastSeen(value);
    setText(value ?? '');
  }

  return (
    <label className="block">
      <span className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-gray-400">
        {label}
      </span>
      <textarea
        rows={2}
        value={text}
        onChange={(e) => { setText(e.target.value); onCommit(e.target.value); }}
        placeholder={placeholder}
        title={text}
        data-ring={ring}
        className="w-full resize-y rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs leading-snug outline-none placeholder:text-gray-300 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/15"
      />
    </label>
  );
}

function StatusPill({ scored }) {
  if (!scored.attended) {
    return <span className="rounded-full border border-gray-300 px-2 py-0.5 text-[11px] font-medium text-gray-500">ABSENT</span>;
  }
  if (!scored.complete) {
    return <span className="rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-[11px] text-gray-400">Scoring…</span>;
  }
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
      scored.passed ? 'bg-green-600 text-white' : 'border border-amber-500 text-amber-700'
    }`}>
      {scored.statusLabel}
    </span>
  );
}

function SaveDot({ state }) {
  if (!state || state === 'saved') return <span className="w-2" />;
  return (
    <span
      title={state === 'error' ? 'Not saved yet — still trying' : 'Saving'}
      className={`h-2 w-2 rounded-full ${state === 'error' ? 'bg-red-500' : 'bg-amber-400'}`}
    />
  );
}

const LADDER = ['White', 'Yellow', 'Green', 'Blue', 'Red', 'Black'];
const nextOf = (rank) => LADDER[LADDER.indexOf(rank) + 1] || '—';

// ── Offline drafts ───────────────────────────────────────────────────────────
// The venue wifi is unreliable and this runs once a month with parents
// watching. A copy in the browser means a dropped connection, a closed tab or a
// flat battery costs nothing.

function readDraft(ceremonyId) {
  try {
    return JSON.parse(localStorage.getItem(draftKey(ceremonyId)) || '{}');
  } catch {
    return {};
  }
}

function writeDraft(ceremonyId, studentKey, changes) {
  const draft = readDraft(ceremonyId);
  draft[studentKey] = { ...(draft[studentKey] || {}), ...changes };
  try {
    localStorage.setItem(draftKey(ceremonyId), JSON.stringify(draft));
  } catch { /* storage full or blocked — the server save is still in flight */ }
}

/** Forget only the fields that reached the server; anything typed since stays. */
function clearDraft(ceremonyId, studentKey, fields) {
  const draft = readDraft(ceremonyId);
  const entry = draft[studentKey];
  if (!entry) return;

  fields.forEach((field) => { delete entry[field]; });
  if (Object.keys(entry).length === 0) delete draft[studentKey];

  try {
    localStorage.setItem(draftKey(ceremonyId), JSON.stringify(draft));
  } catch { /* nothing to do */ }
}
